import type { ReactNode } from "react";

/**
 * Five tones, one meaning each — the only colours a state may wear:
 *
 *   success  it worked, it is healthy, it is done
 *   warning  it needs attention but nothing is broken (pending, behind, drifted)
 *   danger   it failed, it is missing, it is expired
 *   info     a process is running, or a fact worth knowing (a deployment in progress, never deployed)
 *   neutral  no judgement: a draft, an archive, an unknown, a plain label
 *
 * A page maps ITS states to a tone (a table `state → { tone, label }` next to the
 * page that owns the states); it never picks a colour. The colours themselves
 * live in `src/styles/tokens.css` (`success`, `warning`, `danger`, `info`, `neutral`).
 */
export type Tone = "success" | "warning" | "danger" | "info" | "neutral";

// Literal tables, not `bg-${tone}-bg`: Tailwind finds a class by reading its full
// text in the source, and a computed one is never generated.
// Exported for the one other thing that wears a tone: a `pill` Select (the status
// of a row, changed in place), which is a StatusPill you can open.
export const TONE_FILL = {
  success: "bg-success-bg text-success-fg",
  warning: "bg-warning-bg text-warning-fg",
  danger: "bg-danger-bg text-danger-fg",
  info: "bg-info-bg text-info-fg",
  neutral: "bg-neutral-bg text-neutral-fg",
} as const satisfies Record<Tone, string>;

// A row that takes its tone's fill under the pointer. Only the tone a row can be: the destructive
// entry of a menu (`MenuItem danger`), whose text is already the tone's.
export const TONE_ROW = {
  danger: "text-danger-fg hover:bg-danger-bg",
} as const satisfies Partial<Record<Tone, string>>;

const DOT = {
  success: "bg-success-dot",
  warning: "bg-warning-dot",
  danger: "bg-danger-dot",
  info: "bg-info-dot",
  neutral: "bg-neutral-dot",
} as const satisfies Record<Tone, string>;

// sm = inside a pill; md = beside a row's name (a tree, a list); lg = on a timeline.
const DOT_SIZE = { sm: "h-1.5 w-1.5", md: "h-2 w-2", lg: "h-2.5 w-2.5" } as const;

/**
 * The marker: a small disc in the tone's colour. It is what makes a state
 * readable without the colour of the text (the label carries the words, the dot
 * the hue, and neither is enough alone). `pulse` is for work in progress; it
 * stands still for a reader who asked their system for less motion.
 *
 * DECORATIVE (`aria-hidden`): the words next to it say the state. A dot that is
 * the only thing on screen is not enough for a screen reader, so give the row a
 * text alternative.
 */
export function StatusDot({
  tone,
  size = "sm",
  pulse = false,
  className = "",
}: {
  tone: Tone;
  size?: keyof typeof DOT_SIZE;
  pulse?: boolean;
  /** Layout only (margin, alignment). */
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block shrink-0 rounded-full ${DOT_SIZE[size]} ${DOT[tone]}${pulse ? " motion-safe:animate-pulse" : ""}${className ? ` ${className}` : ""}`}
    />
  );
}

/**
 * A state, written as a word: dot + label on a tone-coloured pill.
 *
 * `dot={false}` is for a LABEL that is not a state (a category, a permission, a
 * channel): a dot says "this is how it is right now", and a tag does not.
 *
 * A pill NEVER wraps (`whitespace-nowrap`): it is a single line by shape. In a table
 * cell the column simply grows to fit it. Without that, a narrow column (the
 * "Status" of the components list is 96 px) folds "No claimed stage" onto two
 * lines and turns the pill into an oval capsule — 21 of 22 rows on dev.
 *
 * `className` is for LAYOUT only (a margin): the tone owns the colour, and the
 * size and padding are the pill's own — two competing utilities are decided by
 * the order of the generated stylesheet, not by the order written here.
 */
export function StatusPill({
  tone,
  children,
  dot = true,
  pulse = false,
  title,
  className = "",
}: {
  tone: Tone;
  children: ReactNode;
  dot?: boolean;
  pulse?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full py-0.5 text-xs font-medium ${dot ? "pl-2 pr-2.5" : "px-2.5"} ${TONE_FILL[tone]}${className ? ` ${className}` : ""}`}
    >
      {dot && <StatusDot tone={tone} pulse={pulse} />}
      {children}
    </span>
  );
}
